import React, { useState } from "react";
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Alert,
  Button,
  Spinner,
} from "reactstrap";
import { mergeContractors } from "common/services/contractors.service";
import { toast } from "react-toastify";

interface DuplicatePairRecord {
  id: string | number;
  contractorA?: { id: string | number; contractorName?: string };
  contractorB?: { id: string | number; contractorName?: string };
}

const BulkMergeContractorDuplicates: React.FC<{
  toggle: () => void;
  isModalOpen: boolean;
  selectedRecords: DuplicatePairRecord[];
  fetchData: () => void;
}> = ({ toggle, isModalOpen, selectedRecords, fetchData }) => {
  const [merging, setMerging] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleBulkMerge = async () => {
    setMerging(true);
    setProgress(0);
    let succeeded = 0;
    let failed = 0;

    // Merge sequentially: pairs can share a contractor, so merging in
    // parallel could operate on a contractor another merge just deleted.
    for (const record of selectedRecords) {
      const contractorAId = record?.contractorA?.id;
      const contractorBId = record?.contractorB?.id;
      if (!contractorAId || !contractorBId) {
        failed += 1;
        setProgress((p) => p + 1);
        continue;
      }
      try {
        await mergeContractors({
          id: String(contractorAId),
          contractorId: String(contractorBId),
        });
        succeeded += 1;
      } catch (e: any) {
        failed += 1;
      } finally {
        setProgress((p) => p + 1);
      }
    }

    setMerging(false);
    toggle();
    fetchData();

    if (failed === 0) {
      toast.success(
        `${succeeded} contractor pair${succeeded === 1 ? "" : "s"} merged successfully`,
        {}
      );
    } else {
      toast.error(
        `${succeeded} merged, ${failed} failed. Some pairs may share a contractor already merged in this batch.`,
        {}
      );
    }
  };

  return (
    <Modal isOpen={isModalOpen} toggle={merging ? undefined : toggle} centered>
      <ModalHeader toggle={merging ? undefined : toggle}>
        Confirm Bulk Merge
      </ModalHeader>
      <ModalBody>
        <Alert color="warning" className="material-shadow">
          This action cannot be undone once it has been completed.
        </Alert>
        <p>
          You are about to merge <strong>{selectedRecords.length}</strong>{" "}
          contractor pair{selectedRecords.length === 1 ? "" : "s"}. In each
          pair, the first contractor is kept and the second is merged into it
          and deleted.
        </p>
        <div
          className="table-responsive border border-dashed rounded p-2 mb-2"
          style={{ maxHeight: "220px", overflowY: "auto" }}
        >
          <table className="table table-sm table-borderless mb-0">
            <tbody>
              {selectedRecords.map((record) => (
                <tr key={record.id}>
                  <td className="fw-medium">
                    {record?.contractorA?.contractorName || "-"}
                  </td>
                  <td>
                    <i className="ri-arrow-left-line align-bottom me-1" />
                  </td>
                  <td className="text-muted">
                    {record?.contractorB?.contractorName || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {merging && (
          <p className="text-muted mb-0">
            Merging {progress} of {selectedRecords.length}...
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          color="light"
          className="material-shadow-none"
          onClick={toggle}
          disabled={merging}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          className="material-shadow-none"
          onClick={handleBulkMerge}
          disabled={merging || selectedRecords.length === 0}
        >
          {merging ? <Spinner size="sm" className="me-2" /> : null}
          Confirm Merge
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BulkMergeContractorDuplicates;
